import { createApp } from '@backstage/frontend-defaults';
import catalogPlugin from '@backstage/plugin-catalog/alpha';
import apiDocsPlugin from '@backstage/plugin-api-docs/alpha';
import techdocsPlugin from '@backstage/plugin-techdocs/alpha';
import catalogUnprocessedEntitiesPlugin from '@backstage/plugin-catalog-unprocessed-entities/alpha';
// Gestão de RBAC, usuários/times e console de administração migraram para o
// dashboard externo (backstage-admin-dashboard). O backend RBAC/user-catalog
// permanece; só o frontend de gestão saiu do portal.
import { convertLegacyPlugin, compatWrapper, convertLegacyRouteRef } from '@backstage/core-compat-api';
import { qetaPlugin, QetaPage } from '@drodil/backstage-plugin-qeta';
import {
  announcementsPlugin,
  AnnouncementsPage,
  AnnouncementsAdminPortal,
} from '@backstage-community/plugin-announcements';
import adrPlugin from '@backstage-community/plugin-adr/alpha';
import changeManagementPlugin from 'backstage-plugin-change-management';
import { EntityContentBlueprint, EntityCardBlueprint } from '@backstage/plugin-catalog-react/alpha';
import { Routes, Route } from 'react-router-dom';

// Wrapper que replica o roteamento interno do plugin usando apenas a API pública
const AnnouncementsRouter = () => (
  <Routes>
    <Route path="admin/*" element={<AnnouncementsAdminPortal />} />
    <Route path="*" element={<AnnouncementsPage />} />
  </Routes>
);
import { navModule } from './modules/nav';
import { mermaidAddonModule } from './modules/mermaidAddon';
import { QetaMermaidObserver } from './qeta/QetaMermaidObserver';
import { qetaRehypePlugins } from './qeta/qetaRehypePlugins';
import { featureFlagsModule } from './featureFlags';
import { SignInPageBlueprint, AnalyticsImplementationBlueprint } from '@backstage/plugin-app-react';
import { discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import { createFrontendModule, PageBlueprint } from '@backstage/frontend-plugin-api';
import { ThemeBlueprint, TranslationBlueprint } from '@backstage/plugin-app-react';
import { UnifiedThemeProvider } from '@backstage/theme';
import PaletteIcon from '@material-ui/icons/Palette';
import TimelineIcon from '@material-ui/icons/Timeline';
import SchoolIcon from '@material-ui/icons/School';
import { odptechTheme, odptechDarkTheme } from './themes/odptechTheme';
import {
  catalogResource,
  catalogReactResource,
  scaffolderResource,
  searchResource,
  userSettingsResource,
  announcementsResource,
  sidebarTitlesPtBR,
} from './translations/ptBR';

// Tela de login custom (padrão do admin-dashboard, só Google).
// Microsoft desativado temporariamente — para reativar, restaure o SignInPage
// padrão com os providers google + microsoft.
const googleSignInModule = createFrontendModule({
  pluginId: 'app',
  extensions: [
    SignInPageBlueprint.make({
      params: {
        loader: async () => {
          const { OdpSignInPage } = await import('./modules/auth/OdpSignInPage');
          return props => <OdpSignInPage {...props} />;
        },
      },
    }),
  ],
});

// Overrides the home plugin's page:home extension (no name = default page)
const homeOverrideModule = createFrontendModule({
  pluginId: 'home',
  extensions: [
    PageBlueprint.makeWithOverrides({
      factory(originalFactory) {
        return originalFactory({
          path: '/',
          loader: async () => {
            const { CustomHomePage } = await import('./components/home/HomePage');
            return <CustomHomePage />;
          },
        });
      },
    }),
  ],
});

// Garante que os temas padrão do Backstage não sejam usados
const _savedTheme = localStorage.getItem('theme');
if (
  !_savedTheme ||
  _savedTheme === 'light' ||
  _savedTheme === 'dark' ||
  _savedTheme === 'outlet-theme' ||
  _savedTheme === 'outlet-dark-theme'
) {
  localStorage.setItem('theme', 'odptech-theme');
}

// Define pt-BR como idioma padrão na primeira vez que o app abre
if (!localStorage.getItem('language')) {
  localStorage.setItem('language', 'pt-BR');
}

const odptechThemeModule = createFrontendModule({
  pluginId: 'app',
  extensions: [
    ThemeBlueprint.make({
      name: 'odptech',
      params: {
        theme: {
          id: 'odptech-theme',
          title: 'ODPTech',
          variant: 'light',
          icon: <PaletteIcon />,
          Provider: ({ children }) => (
            <UnifiedThemeProvider theme={odptechTheme} children={children} />
          ),
        },
      },
    }),
    ThemeBlueprint.make({
      name: 'odptech-dark',
      params: {
        theme: {
          id: 'odptech-dark-theme',
          title: 'ODPTech (Dark)',
          variant: 'dark',
          icon: <PaletteIcon />,
          Provider: ({ children }) => (
            <UnifiedThemeProvider theme={odptechDarkTheme} children={children} />
          ),
        },
      },
    }),
  ],
});

// ADR: usamos a montagem que o próprio plugin publica para o novo sistema de
// frontend (`/alpha`), em vez de converter o plugin legado à mão.
//
// A conversão manual daqui registrava o routeRef `adr.root` numa PageBlueprint
// dedicada cujo loader era um `<div />`. Como o plugin monta cada item da lista
// como link para `${rootLink()}?record=…`, clicar numa ADR saía da entidade e
// caía nessa rota vazia — a tela em branco na aba de ADRs. A montagem oficial
// prende o routeRef à própria aba, então o clique fica na página e o
// EntityAdrContent lê o `?record=` e renderiza o leitor ao lado da lista.
//
// Usar a do plugin em vez de copiá-la também nos tira do caminho: se o
// roteamento mudar numa atualização, vem corrigido.
const doraMetricsModule = createFrontendModule({
  pluginId: 'catalog',
  extensions: [
    EntityCardBlueprint.make({
      name: 'dora',
      params: {
        filter: (entity: any) => entity.kind === 'Component',
        loader: async () => {
          const { DoraMetricsCard } = await import('./components/DoraMetrics');
          return <DoraMetricsCard />;
        },
      },
    }),
    EntityContentBlueprint.make({
      name: 'dora',
      params: {
        path: '/dora',
        title: 'DORA Metrics',
        filter: (entity: any) => entity.kind === 'Component',
        loader: async () => {
          const { DoraMetricsContent } = await import('./components/DoraMetrics');
          return <DoraMetricsContent />;
        },
      },
    }),
  ] as any[],
});

const qetaFeature = convertLegacyPlugin(qetaPlugin, {
  extensions: [
    PageBlueprint.make({
      name: 'qeta',
      params: {
        path: '/qeta',
        routeRef: convertLegacyRouteRef(qetaPlugin.routes.root),
        loader: async () =>
          compatWrapper(
            <QetaMermaidObserver>
              <QetaPage rehypePlugins={qetaRehypePlugins} />
            </QetaMermaidObserver>,
          ),
      },
    }),
  ] as any[],
}) as any;

const devSummaryModule = createFrontendModule({
  pluginId: 'app',
  extensions: [
    PageBlueprint.make({
      name: 'desenvolvimento',
      params: {
        path: '/desenvolvimento',
        loader: async () => {
          const { DevSummaryPage } = await import('./components/DevSummary');
          return <DevSummaryPage />;
        },
      },
    }),
  ],
});

const announcementsFeature = convertLegacyPlugin(announcementsPlugin, {
  extensions: [
    PageBlueprint.make({
      name: 'announcements',
      params: {
        path: '/announcements',
        routeRef: convertLegacyRouteRef(announcementsPlugin.routes.root),
        loader: async () => compatWrapper(<AnnouncementsRouter />),
      },
    }),
  ] as any[],
}) as any;

// Cada página de pluginId 'app' precisa de um `name`: sem ele o id resolve para
// `page:app` e duas páginas colidem — a segunda é descartada silenciosamente
// (aviso EXTENSION_IGNORED) e a rota simplesmente deixa de existir.
const onboardingModule = createFrontendModule({
  pluginId: 'app',
  extensions: [
    PageBlueprint.make({
      // title + icon fazem o item aparecer no menu lateral (posicionado em Sidebar.tsx
      // via nav.take('page:app/onboarding')). noHeader porque a página tem Header próprio.
      name: 'onboarding',
      params: {
        path: '/onboarding',
        title: 'Onboarding',
        icon: <SchoolIcon />,
        noHeader: true,
        loader: async () => {
          const { OnboardingPage } = await import('./components/Onboarding');
          return <OnboardingPage />;
        },
      },
    }),
  ],
});

const squadsModule = createFrontendModule({
  pluginId: 'app',
  extensions: [
    PageBlueprint.make({
      name: 'squads',
      params: {
        // Sem title/icon: sai do menu e continua alcançável por URL, junto com
        // a página de detalhe do squad.
        path: '/squads',
        noHeader: true,
        loader: async () => {
          const { SquadsPage } = await import('./components/Squads');
          return <SquadsPage />;
        },
      },
    }),
    // Value Stream: o painel de métricas ágeis. É o item de menu que substituiu
    // Squads — o painel cobre a value stream inteira, não um squad.
    PageBlueprint.make({
      name: 'value-stream',
      params: {
        path: '/value-stream',
        title: 'Value Stream',
        icon: <TimelineIcon />,
        noHeader: true,
        loader: async () => {
          const { ValueStreamPage } = await import('./components/ValueStream');
          return <ValueStreamPage />;
        },
      },
    }),
    // Página de um squad. Sem title/icon: não deve virar item de menu, só é
    // alcançada clicando num card da grade.
    PageBlueprint.make({
      name: 'squad-detalhe',
      params: {
        path: '/squads/:id',
        noHeader: true,
        loader: async () => {
          const { SquadPage } = await import('./components/Squads');
          return <SquadPage />;
        },
      },
    }),
  ],
});

// Registra na tabela audit_log do plugin user-catalog (consumida pelo dashboard):
// - "navigate": telas visitadas
// - "search": termos buscados e se retornaram resultado (o plugin de busca emite
//   value = numberOfResults) — base da métrica de buscas vazias do dashboard.
const pageViewTrackerModule = createFrontendModule({
  pluginId: 'app',
  extensions: [
    AnalyticsImplementationBlueprint.make({
      params: define => define({
        deps: { discoveryApi: discoveryApiRef, fetchApi: fetchApiRef },
        factory: ({ discoveryApi, fetchApi }) => {
          const post = async (body: unknown) => {
            try {
              const base = await discoveryApi.getBaseUrl('user-catalog');
              await fetchApi.fetch(`${base}/page-view`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
              });
            } catch {
              // Falha ao registrar não deve afetar a navegação do usuário.
            }
          };

          // A busca dispara um evento por tecla digitada; o debounce garante
          // que só o termo "final" da digitação seja registrado.
          let searchTimer: ReturnType<typeof setTimeout> | undefined;

          return {
            captureEvent(event: { action: string; subject: string; value?: number }) {
              if (event.action === 'navigate') {
                post({ path: event.subject });
                return;
              }
              if (event.action === 'search' && event.subject?.trim()) {
                const { subject, value } = event;
                if (searchTimer) clearTimeout(searchTimer);
                searchTimer = setTimeout(() => {
                  post({
                    path: '/search',
                    termo_busca: subject,
                    encontrou_resultado: typeof value === 'number' ? value > 0 : null,
                  });
                }, 1500);
              }
            },
          };
        },
      }),
    }),
  ],
});

const translationsModule = createFrontendModule({
  pluginId: 'app',
  extensions: [
    TranslationBlueprint.make({ name: 'catalog-ptbr',       params: { resource: catalogResource } }),
    TranslationBlueprint.make({ name: 'catalog-react-ptbr', params: { resource: catalogReactResource } }),
    TranslationBlueprint.make({ name: 'scaffolder-ptbr',    params: { resource: scaffolderResource } }),
    TranslationBlueprint.make({ name: 'search-ptbr',        params: { resource: searchResource } }),
    TranslationBlueprint.make({ name: 'user-settings-ptbr', params: { resource: userSettingsResource } }),
    TranslationBlueprint.make({ name: 'announcements-ptbr', params: { resource: announcementsResource } }),
    TranslationBlueprint.make({ name: 'sidebar-ptbr',       params: { resource: sidebarTitlesPtBR } }),
  ],
});

export default createApp({
  features: [
    catalogPlugin,
    doraMetricsModule,
    apiDocsPlugin,
    techdocsPlugin,
    catalogUnprocessedEntitiesPlugin,
    adrPlugin,
    changeManagementPlugin,
    announcementsFeature,
    qetaFeature,
    devSummaryModule,
    homeOverrideModule,
    onboardingModule,
    squadsModule,
    pageViewTrackerModule,
    navModule,
    googleSignInModule,
    featureFlagsModule,
    odptechThemeModule,
    translationsModule,
    mermaidAddonModule,
  ],
});
